/**
 * Permit Status List API Contract
 *
 * ts-rest contract definition for the permit status options endpoint.
 *
 * Endpoint  : GET /jobs/permit-status
 * Method    : GET
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Array of active permit statuses ordered by sequence
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single permit status record
 */
export const permitStatusItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  sequence: z.number().nullable(),
  color: z.string().nullable(),
});

/**
 * Zod schema for the permit status list response
 */
export const permitStatusListResponseSchema = z.array(permitStatusItemSchema);

/**
 * TypeScript types inferred from Zod schemas
 */
export type PermitStatusItem = z.infer<typeof permitStatusItemSchema>;
export type PermitStatusListResponse = z.infer<typeof permitStatusListResponseSchema>;

/**
 * Permit Status List API Contract
 *
 * Base path: /jobs/permit-status  (mounted in apps/backend/src/routes/index.ts)
 */
export const permitStatusContract = c.router({
  /**
   * Get all active permit statuses
   *
   * GET /jobs/permit-status
   *
   * Returns every permit_status row where is_active = true,
   * sorted by sequence ascending.
   *
   * Used by: works-lite permit status dropdown.
   */
  getPermitStatusList: {
    method: 'GET',
    path: '/',
    responses: {
      200: permitStatusListResponseSchema,
      500: z.object({
        error: z.object({
          message: z.string(),
          code: z.string().optional(),
        }),
      }),
    },
    summary: 'Get all active permit statuses ordered by sequence',
    description:
      'Returns all permit_status rows where is_active = true, ordered by sequence ascending.',
  },
});
